import React from 'react';
import { X } from 'lucide-react';
import { DMA_REGIONS } from '../../../data/dmaList';
import { US_STATE_MAP } from '../../../data/usStates';
import type { SelfServiceFormData } from '../../../types/selfService';
import PrimaryButton from './PrimaryButton';

type GeoMode = NonNullable<SelfServiceFormData['geoMode']>;

interface TabSummary {
  mode: GeoMode;
  label: string;
  items: string[];
}

interface GeoConflictModalProps {
  /** Picker lists pulled from form state. */
  selectedStates: string[];
  selectedDMAs: string[];
  radiusZips: string[];
  /** Currently selected tab — pre-selects the option in the modal. */
  currentMode: GeoMode;
  accentColor: string;
  onCancel: () => void;
  /** User picked a tab. Caller should set geoMode and re-advance. */
  onConfirm: (mode: GeoMode) => void;
}

function dmaLabel(id: string): string {
  const dma = DMA_REGIONS.find((d) => d.id === id);
  return dma ? `${dma.code} — ${dma.name}` : id;
}

function stateLabel(code: string): string {
  const name = US_STATE_MAP[code];
  return name ? `${code} — ${name}` : code;
}

// Mirrors the mockup's NewGeoTargeting conflict modal. When the user's
// picker lists span multiple tabs at submit time, the campaign can only
// commit to one — this prompts them to pick which.
const GeoConflictModal: React.FC<GeoConflictModalProps> = ({
  selectedStates,
  selectedDMAs,
  radiusZips,
  currentMode,
  accentColor,
  onCancel,
  onConfirm,
}) => {
  const tabs = React.useMemo<TabSummary[]>(() => {
    const out: TabSummary[] = [];
    if (radiusZips.length > 0) out.push({ mode: 'radius', label: 'Radius', items: radiusZips });
    if (selectedStates.length > 0) out.push({ mode: 'states', label: 'States', items: selectedStates.map(stateLabel) });
    if (selectedDMAs.length > 0) out.push({ mode: 'dmas', label: 'DMAs', items: selectedDMAs.map(dmaLabel) });
    return out;
  }, [radiusZips, selectedStates, selectedDMAs]);

  const [pending, setPending] = React.useState<GeoMode>(currentMode);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-1">
          <h3 className="text-lg font-semibold text-slate-900">Multiple targeting types selected</h3>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          You have geographic data on more than one tab. Pick which one to use for this campaign.
        </p>

        <div className="space-y-3">
          {tabs.map((tab) => {
            const isSelected = pending === tab.mode;
            return (
              <button
                key={tab.mode}
                type="button"
                onClick={() => setPending(tab.mode)}
                className="w-full text-left p-4 rounded-lg border-2 transition-all"
                style={{
                  borderColor: isSelected ? accentColor : 'rgb(226 232 240)',
                  backgroundColor: isSelected ? 'rgba(59,130,246,0.06)' : 'white',
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-slate-900">{tab.label}</span>
                  <span className="text-xs text-slate-500">{tab.items.length} selected</span>
                </div>
                <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                  {tab.items.slice(0, 40).map((item, i) => (
                    <span
                      key={`${tab.mode}-${i}-${item}`}
                      className="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-600"
                    >
                      {item}
                    </span>
                  ))}
                  {tab.items.length > 40 && (
                    <span className="text-[10px] text-slate-500 px-1.5 py-0.5">
                      …and {tab.items.length - 40} more
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-5 flex gap-2 justify-end items-center">
          <button
            type="button"
            onClick={onCancel}
            className="px-3.5 py-1.5 rounded-full text-[12px] font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <PrimaryButton
            type="button"
            onClick={() => onConfirm(pending)}
            accentColor={accentColor}
          >
            Use {tabs.find((t) => t.mode === pending)?.label ?? 'this'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default GeoConflictModal;
